import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Users, Brain, Trash, ArrowsClockwise, X, Hash, Chat,
  Heart, Notebook, ShieldCheck,
} from "@phosphor-icons/react";

export default function CustomerMemory() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/memory/users");
      setProfiles(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!openId) { setDetail(null); return; }
    setDetailLoading(true);
    api.get(`/admin/memory/users/${openId}`)
      .then(({ data }) => setDetail(data))
      .catch(() => toast.error("Couldn't load profile"))
      .finally(() => setDetailLoading(false));
  }, [openId]);

  const forget = async (userId, email) => {
    if (!window.confirm(`Erase all memory for ${email}? This removes their profile, sessions and events. Cannot be undone.`)) return;
    try {
      await api.delete(`/admin/memory/users/${userId}`);
      toast.success("Memory erased");
      setOpenId(null);
      load();
    } catch {
      toast.error("Failed to erase");
    }
  };

  return (
    <div className="space-y-8" data-testid="customer-memory-page">
      <div>
        <div className="mono-label text-orange-600 mb-2">// governance · customer memory</div>
        <h1 className="font-display text-4xl md:text-5xl text-slate-900 tracking-tight leading-tight">
          Customer Memory
        </h1>
        <p className="text-slate-600 mt-2 max-w-2xl">
          Audit what the Relationship Desk has remembered about each user. Inspect preferences,
          recurring concerns, recent conversations, and exercise the right-to-forget.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Metric label="Profiles" value={profiles.length} />
        <Metric label="Active today" value={profiles.filter((p) => isToday(p.last_seen_iso)).length} tone="emerald" />
        <Metric label="Total conversations" value={profiles.reduce((a, p) => a + (p.conversation_count || 0), 0)} />
        <Metric label="Known facts stored" value={profiles.reduce((a, p) => a + (p.known_facts?.length || 0), 0)} tone="orange" />
      </div>

      <div className="border border-slate-200 bg-white">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="mono-label flex items-center gap-2">
            <Users size={14} className="text-orange-600" />
            Customer profiles ({profiles.length})
          </div>
          <button onClick={load} className="btn-sharp btn-ghost text-xs" data-testid="customer-memory-refresh">
            <ArrowsClockwise size={12} />
          </button>
        </div>
        {loading ? (
          <div className="p-12 text-center mono-label pulse-soft">Loading…</div>
        ) : profiles.length === 0 ? (
          <div className="p-12 text-center text-slate-400 text-sm">No user profiles yet.</div>
        ) : (
          <div data-testid="memory-profile-list">
            {profiles.map((p) => (
              <button
                key={p.user_id}
                onClick={() => setOpenId(p.user_id)}
                className="w-full text-left px-5 py-4 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-all flex items-start gap-3"
                data-testid={`profile-row-${p.user_id}`}
              >
                <div
                  className="w-9 h-9 shrink-0 flex items-center justify-center font-display text-sm"
                  style={{ background: "#F3F1EC", color: "#9E8155", borderRadius: 2 }}
                >
                  {(p.email || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-900 flex items-center gap-2">
                    <span className="font-medium">{p.email}</span>
                    {p.role && (
                      <span className="font-mono text-xs px-1.5 py-0.5 bg-slate-100 text-slate-600">
                        {p.role}
                      </span>
                    )}
                  </div>
                  <div className="mono-label mt-0.5 truncate">
                    {p.conversation_count || 0} conversations · last seen {fmtDate(p.last_seen_iso)}
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {(p.preferred_brands || []).slice(0, 4).map((b) => (
                      <span key={b} className="text-[11px] px-1.5 py-0.5 bg-orange-50 text-orange-700 border border-orange-200">
                        {b}
                      </span>
                    ))}
                    {(p.preferred_topics || []).slice(0, 3).map((t) => (
                      <span key={t} className="text-[11px] px-1.5 py-0.5 bg-slate-100 text-slate-700 border border-slate-200">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="font-display text-xl text-slate-900">{p.known_facts?.length || 0}</div>
                  <div className="mono-label">facts</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* detail drawer */}
      {openId && (
        <div
          className="fixed inset-0 z-30 bg-slate-900/50 flex justify-end"
          onClick={() => setOpenId(null)}
          data-testid="memory-drawer"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-3xl bg-white h-full overflow-y-auto border-l border-slate-200"
          >
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <div className="mono-label text-orange-600">// profile detail</div>
                <div className="font-mono text-xs text-slate-500 mt-1">{openId}</div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => detail && forget(openId, detail.profile?.email || openId)}
                  disabled={!detail}
                  className="btn-sharp btn-secondary text-sm text-rose-700 border-rose-200 disabled:opacity-40"
                  data-testid="memory-drawer-forget"
                >
                  <Trash size={14} /> Erase memory
                </button>
                <button onClick={() => setOpenId(null)} className="btn-sharp btn-ghost" data-testid="memory-drawer-close">
                  <X size={14} />
                </button>
              </div>
            </div>
            {detailLoading || !detail ? (
              <div className="p-12 text-center mono-label pulse-soft">Loading profile…</div>
            ) : (
              <div className="p-6 space-y-6">
                <div>
                  <div className="mono-label mb-1">Customer</div>
                  <div className="text-sm text-slate-900">{detail.profile.email}</div>
                  <div className="mono-label">Member since {fmtDate(detail.profile.first_seen_iso)} · {detail.profile.conversation_count || 0} conversations · tone: {detail.profile.tone_preference}</div>
                </div>

                <Section icon={Heart} label="Preferred brands" items={detail.profile.preferred_brands} testid="mem-brands" empty="No brand preferences detected yet." />
                <Section icon={Hash} label="Recurring topics" items={detail.profile.preferred_topics} testid="mem-topics" empty="No recurring topics." />
                <Section icon={ShieldCheck} label="Concerns surfaced" items={detail.profile.recurring_concerns} testid="mem-concerns" tone="rose" empty="No recurring concerns noted." />

                <div>
                  <div className="mono-label mb-2 flex items-center gap-2">
                    <Brain size={12} className="text-orange-600" />
                    Facts I remember ({detail.profile.known_facts?.length || 0})
                  </div>
                  {(detail.profile.known_facts || []).length === 0 ? (
                    <div className="text-sm italic text-slate-400">Nothing durable stored yet.</div>
                  ) : (
                    <ul className="space-y-1" data-testid="mem-facts">
                      {detail.profile.known_facts.map((f, i) => (
                        <li key={i} className="text-sm text-slate-900 border border-slate-200 bg-slate-50 px-3 py-2">
                          {f}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div>
                  <div className="mono-label mb-2 flex items-center gap-2">
                    <Notebook size={12} className="text-orange-600" />
                    Recent conversations ({detail.sessions?.length || 0})
                  </div>
                  {(detail.sessions || []).length === 0 ? (
                    <div className="text-sm italic text-slate-400">No conversations yet.</div>
                  ) : (
                    <div className="space-y-1" data-testid="mem-sessions">
                      {detail.sessions.map((s) => (
                        <div key={s.id} className="border border-slate-200 px-3 py-2 text-xs">
                          <div className="text-sm text-slate-900 font-medium truncate">{s.title || "(untitled)"}</div>
                          <div className="mono-label">
                            {s.turn_count || 0} exchanges · {fmtDate(s.updated_at)}
                            {s.sentiment && s.sentiment !== "neutral" ? ` · ${s.sentiment}` : ""}
                          </div>
                          {s.topic_tags?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {s.topic_tags.slice(0, 6).map((t) => (
                                <span key={t} className="text-[10px] px-1 py-0.5 bg-slate-100 text-slate-700">{t}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <div className="mono-label mb-2 flex items-center gap-2">
                    <Chat size={12} className="text-orange-600" />
                    Latest turns ({detail.recent_events?.length || 0})
                  </div>
                  <div className="space-y-1" data-testid="mem-events">
                    {(detail.recent_events || []).slice(0, 20).map((e) => (
                      <div key={e.id} className="border border-slate-200 px-3 py-2 text-xs">
                        <div className="text-slate-900 font-medium">Q: {e.query}</div>
                        <div className="text-slate-600 mt-1 line-clamp-2">A: {e.answer}</div>
                        <div className="mono-label mt-1">
                          ₹{Number(e.cost_inr || 0).toFixed(4)} · {e.tokens || 0} tok · {e.sentiment || "neutral"} · {fmtDate(e.created_at)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, tone = "slate" }) {
  const color =
    tone === "emerald" ? "text-emerald-700"
      : tone === "orange" ? "text-orange-700"
      : "text-slate-900";
  return (
    <div className="border border-slate-200 bg-white p-4">
      <div className="mono-label">{label}</div>
      <div className={`font-display text-2xl mt-1 ${color}`}>{value}</div>
    </div>
  );
}

function Section({ icon: Icon, label, items, testid, tone = "orange", empty }) {
  const chipCls =
    tone === "rose" ? "bg-rose-50 text-rose-800 border-rose-200"
      : "bg-orange-50 text-orange-800 border-orange-200";
  return (
    <div>
      <div className="mono-label mb-2 flex items-center gap-2">
        <Icon size={12} className="text-orange-600" />
        {label} ({(items || []).length})
      </div>
      {(items || []).length === 0 ? (
        <div className="text-sm italic text-slate-400">{empty}</div>
      ) : (
        <div className="flex flex-wrap gap-1" data-testid={testid}>
          {items.map((x) => (
            <span key={x} className={`text-xs px-2 py-0.5 border ${chipCls}`}>{x}</span>
          ))}
        </div>
      )}
    </div>
  );
}

function isToday(iso) {
  if (!iso) return false;
  try {
    const d = new Date(iso);
    const n = new Date();
    return d.toDateString() === n.toDateString();
  } catch { return false; }
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return "—"; }
}
